import { NavTemps } from './temps/rhCommun'; // chantier temps (agent I) : menu Temps, historique en lecture seule
import { BandeauInfo } from '../design/composants';
import { useState, useEffect, useCallback } from 'react';
import usePerimetre from '../hooks/usePerimetre';
import BandeauPerimetre from '../components/BandeauPerimetre';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Pencil, Clock, Flag, Sun, Camera, PenLine, AlertTriangle, TrendingUp, Download, FileText, Lock, Unlock, Check } from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import { useFichierSecurise } from '../utils/fichiersSecurises';

const API = process.env.REACT_APP_API_URL || '';

const COLONNES = [
  { key: 'h_normales',      label: 'H.NORM',    color: '#1B5E20' },
  { key: 'h_hs_15',         label: 'HS 15%',    color: '#F57F17' },
  { key: 'h_hs_40',         label: 'HS 40%',    color: '#BF360C' },
  { key: 'h_dimanche',      label: 'H.DIM',     color: '#1565C0' },
  { key: 'h_nuit_normal',   label: 'H.NUIT N',  color: '#0D47A1' },
  { key: 'h_nuit_dim',      label: 'H.NUIT D',  color: '#1A237E' },
  { key: 'h_absent_injust', label: 'Absences',  color: '#C62828' },
  { key: 'nb_paniers',      label: 'Paniers',   color: '#558B2F' },
];

const MOTIFS_CORRECTION = [
  'Oubli de pointage', 'Panne QR Code', 'Absence justifiée',
  'Jour férié non enregistré', 'Correction administrative', 'Autre',
];

const ROLES_RH = ['admin', 'drh', 'rh_principal', 'assistant_rh', 'directeur_general', 'directeur_adjoint'];
const JOURS_SEMAINE = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

function fmt2(val) {
  const n = parseFloat(val || 0);
  return n === 0 ? '—' : n.toFixed(2);
}

function couleurLigne(recap) {
  if (parseFloat(recap.h_absent_injust || 0) > 0) return '#FFF3E0';
  if (parseFloat(recap.h_hs_15 || 0) > 0 || parseFloat(recap.h_hs_40 || 0) > 0) return '#FFFDE7';
  if (parseFloat(recap.h_conge || 0) > 0) return '#E3F2FD';
  return '#fff';
}

// ─── Skeleton ────────────────────────────────────────────────────────────────
function Skeleton({ w = '100%', h = 16, mb = 8 }) {
  return (
    <div style={{
      width: w, height: h, marginBottom: mb,
      background: 'linear-gradient(90deg,#e0e0e0 25%,#f5f5f5 50%,#e0e0e0 75%)',
      backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', borderRadius: 4,
    }} />
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────
// Même texte que la réponse du serveur (409 CLOTURE_SUSPENDUE) : la clôture mensuelle par site remplace la clôture hebdomadaire.
const MESSAGE_CLOTURE_SUSPENDUE = 'La clôture hebdomadaire n\'existe plus : le temps se clôture désormais par mois et par site (Temps › Heures et clôture).';

function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  const bg = type === 'success' ? '#1B5E20' : type === 'error' ? '#C62828' : '#1565C0';
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, background: bg, color: '#fff',
      padding: '12px 20px', borderRadius: 10, zIndex: 9999, fontSize: 14,
      boxShadow: '0 4px 16px rgba(0,0,0,0.25)', maxWidth: 360,
    }}>
      {msg}
    </div>
  );
}

// ─── Badge ────────────────────────────────────────────────────────────────────
function Badge({ label, color }) {
  return (
    <span style={{
      background: color + '22', color, border: `1px solid ${color}44`,
      borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600,
    }}>
      {label}
    </span>
  );
}

// ─── StatCard ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, color, sub }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '12px 14px',
      boxShadow: '0 1px 6px rgba(0,0,0,0.06)', borderLeft: `3px solid ${color}`,
    }}>
      <div style={{ fontSize: '0.6rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 4, fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ fontSize: '1.5rem', fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.65rem', color: '#999', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// ─── TabBar ───────────────────────────────────────────────────────────────────
function TabBar({ onglets, actif, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 2, background: '#f0f0f0', borderBottom: '2px solid #ddd', padding: '0 14px' }}>
      {onglets.map(o => (
        <button key={o.id} onClick={() => onChange(o.id)} style={{
          padding: '9px 18px', border: 'none', borderRadius: '8px 8px 0 0',
          background: actif === o.id ? '#fff' : 'transparent',
          color: actif === o.id ? '#1B5E20' : '#666',
          fontWeight: actif === o.id ? 700 : 400,
          fontSize: 13, cursor: 'pointer',
          borderBottom: actif === o.id ? '2px solid #1B5E20' : '2px solid transparent',
          marginBottom: -2,
        }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ─── ModalCorrection ─────────────────────────────────────────────────────────
function ModalCorrection({ jour, onClose, onSaved, token }) {
  const [arrivee, setArrivee] = useState(
    jour.heure_arrivee ? new Date(jour.heure_arrivee).toISOString().slice(0, 16) : ''
  );
  const [depart, setDepart] = useState(
    jour.heure_depart ? new Date(jour.heure_depart).toISOString().slice(0, 16) : ''
  );
  const [motifChoisi, setMotifChoisi] = useState(MOTIFS_CORRECTION[0]);
  const [motifDetail, setMotifDetail] = useState('');
  const [saving, setSaving] = useState(false);
  const [errMotif, setErrMotif] = useState('');

  const motifFinal = motifChoisi === 'Autre' && motifDetail.trim()
    ? `Autre : ${motifDetail.trim()}` : motifChoisi;

  const handleSave = async () => {
    if (motifChoisi === 'Autre' && !motifDetail.trim()) { setErrMotif('Veuillez préciser le motif de correction.'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/api/temps/saisie/${jour.id}`, {
        heure_arrivee: arrivee || null,
        heure_depart: depart || null,
        motif_modification: motifFinal,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved('Heures corrigées avec succès', 'success');
      onClose();
    } catch (e) {
      onSaved(e.response?.data?.message || 'Erreur lors de la modification', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 440, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
        <h3 style={{ margin: '0 0 16px', color: '#1B5E20', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Pencil size={16} />Corriger les heures
        </h3>
        <p style={{ fontSize: 12, color: '#666', marginBottom: 16 }}>
          {new Date(jour.date_jour).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
        <label style={lbS}>Heure d'arrivée</label>
        <input type="datetime-local" value={arrivee} onChange={e => setArrivee(e.target.value)} style={inS} />
        <label style={lbS}>Heure de départ</label>
        <input type="datetime-local" value={depart} onChange={e => setDepart(e.target.value)} style={inS} />
        <label style={lbS}>Motif de correction <span style={{ color: '#C62828' }}>*</span></label>
        <select value={motifChoisi} onChange={e => setMotifChoisi(e.target.value)} style={inS}>
          {MOTIFS_CORRECTION.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        {motifChoisi === 'Autre' && (
          <>
            <label style={lbS}>Préciser <span style={{ color: '#C62828' }}>*</span></label>
            <textarea value={motifDetail} onChange={e => { setMotifDetail(e.target.value); if (errMotif) setErrMotif(''); }}
              placeholder="Détail du motif..." style={{ ...inS, height: 60, resize: 'vertical', borderColor: errMotif ? '#C62828' : undefined }} />
            {errMotif && <div style={{ fontSize: 12, color: '#C62828', marginTop: 4 }}>{errMotif}</div>}
          </>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose} style={btnSec}>Annuler</button>
          <button onClick={handleSave} disabled={saving} style={btnPri}>
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── PanneauDetail ────────────────────────────────────────────────────────────
function PanneauDetail({ recap, semaineCloturee, peutSaisir, onClose, onCorrection, onSaved, token }) {
  const jours = recap.jours || [];
  return (
    <div style={{
      position: 'fixed', right: 0, top: 0, bottom: 0, width: 520, maxWidth: '95vw',
      background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)', zIndex: 1500,
      overflowY: 'auto', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ background: '#1B5E20', color: '#fff', padding: '16px 20px', position: 'sticky', top: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{recap.prenom} {recap.nom}</div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>{recap.matricule} — {recap.site_nom || ''}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
      </div>
      <div style={{ padding: '16px 20px', flex: 1 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 20 }}>
          {COLONNES.map(c => (
            <div key={c.key} style={{ background: '#f9f9f9', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#666' }}>{c.label}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: c.color }}>{fmt2(recap[c.key])}</div>
            </div>
          ))}
        </div>
        <h4 style={{ color: '#1B5E20', margin: '0 0 10px' }}>Détail par jour</h4>
        {jours.length === 0 && <p style={{ color: '#999', fontSize: 13 }}>Aucune donnée pour cette semaine.</p>}
        {jours.map((j, i) => {
          const dateObj = new Date(j.date_jour);
          const nomJour = JOURS_SEMAINE[dateObj.getDay() === 0 ? 6 : dateObj.getDay() - 1];
          const hArr = j.heure_arrivee ? new Date(j.heure_arrivee).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—';
          const hDep = j.heure_depart  ? new Date(j.heure_depart).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—';
          const anomalie = (j.heure_arrivee && !j.heure_depart);
          return (
            <div key={j.id || i} style={{
              border: `1px solid ${anomalie ? '#C62828' : '#e0e0e0'}`,
              borderRadius: 10, padding: '10px 14px', marginBottom: 10,
              background: anomalie ? '#FFEBEE' : (j.est_dimanche ? '#EDE7F6' : (j.est_ferie ? '#FFF8E1' : '#fff')),
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{nomJour}</span>
                  <span style={{ fontSize: 12, color: '#666', marginLeft: 8 }}>
                    {dateObj.toLocaleDateString('fr-FR')}
                    {j.est_ferie && <span style={{ marginLeft: 6, color: '#6A1B9A', display: 'inline-flex', alignItems: 'center', gap: 3 }}><Flag size={11} />Férié</span>}
                    {j.est_dimanche && <span style={{ marginLeft: 6, color: '#1565C0', display: 'inline-flex', alignItems: 'center', gap: 3 }}><Sun size={11} />Dim</span>}
                  </span>
                </div>
                {peutSaisir && !semaineCloturee && (
                  <button onClick={() => onCorrection(j)} style={{ ...btnSec, fontSize: 11, padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Pencil size={11} />Corriger
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 13 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#1B5E20', display: 'inline-block' }} />{hArr}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#C62828', display: 'inline-block' }} />{hDep}
                </span>
                <span style={{ color: '#666', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  {j.source === 'qr_code' ? <><Camera size={10} />QR</> : <><PenLine size={10} />Manuel</>}
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                {parseFloat(j.h_normales || 0) > 0 && <Badge label={`${fmt2(j.h_normales)}h norm`} color="#1B5E20" />}
                {parseFloat(j.h_dimanche || 0) > 0 && <Badge label={`${fmt2(j.h_dimanche)}h dim`} color="#1565C0" />}
                {parseFloat(j.h_ferie_jour || 0) > 0 && <Badge label={`${fmt2(j.h_ferie_jour)}h férié`} color="#6A1B9A" />}
                {parseFloat(j.h_nuit_normal || 0) > 0 && <Badge label={`${fmt2(j.h_nuit_normal)}h nuit`} color="#0D47A1" />}
                {j.nb_paniers > 0 && <Badge label={`${j.nb_paniers} panier`} color="#558B2F" />}
                {parseFloat(j.h_absent_injust || 0) > 0 && <Badge label="Abs.injust" color="#C62828" />}
                {anomalie && <Badge label="Sans départ" color="#C62828" />}
              </div>
              {j.motif_modification && (
                <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ background: '#FFF3E0', color: '#E65100', border: '1px solid #FFB74D', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                    Modifié
                  </span>
                  <span style={{ fontSize: 11, color: '#666', fontStyle: 'italic' }}>{j.motif_modification}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Tableau semaine partagé ──────────────────────────────────────────────────
function TableauSemaine({ recaps, loading, semaineCloturee, totaux, setEmployeDetail }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
        <thead>
          <tr style={{ background: '#1B5E20', color: '#fff' }}>
            <th style={thS}>Employé</th>
            <th style={thS}>Shift</th>
            {COLONNES.map(c => <th key={c.key} style={thS}>{c.label}</th>)}
            <th style={thS}>Statut</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array(6).fill(0).map((_, i) => (
              <tr key={i}>{Array(COLONNES.length + 3).fill(0).map((_, j) => (
                <td key={j} style={{ padding: 10 }}><Skeleton h={14} /></td>
              ))}</tr>
            ))
          ) : recaps.length === 0 ? (
            <tr>
              <td colSpan={COLONNES.length + 3} style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                Aucune donnée pour cette semaine.
              </td>
            </tr>
          ) : (
            Array.isArray(recaps) && recaps.map(r => {
              const anomalie = (r.jours || []).some(j => j.heure_arrivee && !j.heure_depart);
              const hsDetect = parseFloat(r.h_hs_15 || 0) > 0 || parseFloat(r.h_hs_40 || 0) > 0;
              const bgRow = semaineCloturee ? '#f5f5f5'
                : anomalie ? '#FFEBEE' : couleurLigne(r);
              return (
                <tr key={r.employe_id}
                  onClick={() => setEmployeDetail(r)}
                  style={{ background: bgRow, borderBottom: '1px solid #f0f0f0', cursor: 'pointer', opacity: semaineCloturee ? 0.75 : 1 }}
                  onMouseEnter={e => { if (!semaineCloturee) e.currentTarget.style.background = '#E8F5E9'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = bgRow; }}>
                  <td style={tdS}>
                    <div style={{ fontWeight: 600, color: semaineCloturee ? '#999' : '#222' }}>{r.prenom} {r.nom}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{r.matricule}</div>
                  </td>
                  <td style={{ ...tdS, fontSize: 12, color: '#555' }}>{r.shift_nom || '—'}</td>
                  {COLONNES.map(c => (
                    <td key={c.key} style={{
                      ...tdS, textAlign: 'center',
                      color: semaineCloturee ? '#bbb' : (parseFloat(r[c.key] || 0) > 0 ? c.color : '#ccc'),
                      fontWeight: parseFloat(r[c.key] || 0) > 0 ? 700 : 400,
                    }}>
                      {fmt2(r[c.key])}
                    </td>
                  ))}
                  <td style={{ ...tdS, textAlign: 'center' }}>
                    {semaineCloturee
                      ? <Lock size={13} color="#bbb" title="Semaine clôturée" />
                      : anomalie ? <AlertTriangle size={14} color="#C62828" title="Arrivée sans départ" />
                      : hsDetect ? <TrendingUp size={14} color="#F57F17" title="HS détectées" />
                      : <Check size={14} color="#1B5E20" />}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
        {!loading && recaps.length > 0 && (
          <tfoot>
            <tr style={{ background: '#E8F5E9', fontWeight: 700 }}>
              <td style={tdS} colSpan={2}>TOTAUX ({recaps.length} emp.)</td>
              {COLONNES.map(c => (
                <td key={c.key} style={{ ...tdS, textAlign: 'center', color: '#1B5E20' }}>
                  {fmt2(totaux[c.key])}
                </td>
              ))}
              <td style={tdS} />
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

// ─── Vue Employé (personnel) ─────────────────────────────────────────────────
function VueEmployeContent({ employe, myRecap, loading, offsetSemaine, setOffsetSemaine, labelSemaine }) {
  const [tabEmp, setTabEmp] = useState('semaine');
  const photoSecurisee = useFichierSecurise(employe?.photo_url || null);

  const TABS_EMP = [
    { id: 'semaine', label: 'Cette semaine' },
    { id: 'hs',      label: 'Mes HS' },
    { id: 'paniers', label: 'Mes paniers' },
    { id: 'absences',label: 'Mes absences' },
  ];

  return (
    <div>
      {/* Carte profil */}
      <div style={{ background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16 }}>
        {photoSecurisee ? (
          <img src={photoSecurisee} alt="" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', border: '3px solid #1B5E20' }} />
        ) : (
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#E8F5E9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#1B5E20" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
            </svg>
          </div>
        )}
        <div>
          <div style={{ fontWeight: 700, fontSize: 18, color: '#1B5E20' }}>{employe.prenom} {employe.nom}</div>
          <div style={{ fontSize: 13, color: '#666' }}>{employe.poste || 'Employé'} • {employe.matricule}</div>
          {employe.departement && <div style={{ fontSize: 12, color: '#999' }}>{employe.departement}</div>}
        </div>
      </div>

      {/* Navigateur semaine */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <button onClick={() => setOffsetSemaine(o => o - 1)} style={{ ...btnSec, padding: '6px 10px' }}>‹</button>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#333', flex: 1, textAlign: 'center' }}>{labelSemaine}</span>
        <button onClick={() => setOffsetSemaine(o => o + 1)} style={{ ...btnSec, padding: '6px 10px' }}>›</button>
        {offsetSemaine !== 0 && (
          <button onClick={() => setOffsetSemaine(0)} style={{ ...btnSec, fontSize: 11, padding: '5px 10px' }}>Auj.</button>
        )}
      </div>

      {/* Onglets employé */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '2px solid #e0e0e0', marginBottom: 12 }}>
        {TABS_EMP.map(t => (
          <button key={t.id} onClick={() => setTabEmp(t.id)} style={{
            padding: '8px 14px', border: 'none', background: 'transparent',
            color: tabEmp === t.id ? '#1B5E20' : '#666',
            fontWeight: tabEmp === t.id ? 700 : 400,
            fontSize: 13, cursor: 'pointer',
            borderBottom: tabEmp === t.id ? '2px solid #1B5E20' : '2px solid transparent',
            marginBottom: -2,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ background: '#fff', borderRadius: 14, padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          {Array(4).fill(0).map((_, i) => <Skeleton key={i} h={20} mb={12} />)}
        </div>
      ) : !myRecap ? (
        <div style={{ background: '#fff', borderRadius: 14, padding: 40, textAlign: 'center', color: '#999', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          Aucune donnée pour cette semaine.
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>

          {/* ── Semaine ── */}
          {tabEmp === 'semaine' && (
            <div style={{ padding: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 20 }}>
                <div style={{ background: '#E8F5E9', borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>H. normales</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#1B5E20' }}>{fmt2(myRecap.h_normales)}</div>
                </div>
                <div style={{ background: '#FFF3E0', borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>Heures sup.</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#F57F17' }}>
                    {fmt2(parseFloat(myRecap.h_hs_15 || 0) + parseFloat(myRecap.h_hs_40 || 0))}
                  </div>
                </div>
                <div style={{ background: '#E3F2FD', borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>H. de nuit</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#0D47A1' }}>
                    {fmt2(parseFloat(myRecap.h_nuit_normal || 0) + parseFloat(myRecap.h_nuit_dim || 0))}
                  </div>
                </div>
                <div style={{ background: '#F1F8E9', borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>Paniers</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#558B2F' }}>{Math.round(myRecap.nb_paniers || 0)}</div>
                </div>
              </div>
              <h4 style={{ color: '#1B5E20', margin: '0 0 12px', fontSize: 14 }}>Détail des jours</h4>
              {(myRecap.jours || []).map((j, i) => {
                const dateObj = new Date(j.date_jour);
                const nomJour = JOURS_SEMAINE[dateObj.getDay() === 0 ? 6 : dateObj.getDay() - 1];
                const hArr = j.heure_arrivee ? new Date(j.heure_arrivee).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—';
                const hDep = j.heure_depart  ? new Date(j.heure_depart).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—';
                const anomalie = j.heure_arrivee && !j.heure_depart;
                return (
                  <div key={j.id || i} style={{
                    border: `1px solid ${anomalie ? '#C62828' : '#e0e0e0'}`,
                    borderRadius: 10, padding: '10px 14px', marginBottom: 8,
                    background: anomalie ? '#FFEBEE' : '#fafafa',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 700, fontSize: 13 }}>{nomJour} {dateObj.toLocaleDateString('fr-FR')}</span>
                      <div style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                        <span style={{ color: '#1B5E20' }}>{hArr}</span>
                        <span style={{ color: '#ccc' }}>→</span>
                        <span style={{ color: '#C62828' }}>{hDep}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>
                      {parseFloat(j.h_normales || 0) > 0 && <Badge label={`${fmt2(j.h_normales)}h`} color="#1B5E20" />}
                      {parseFloat(j.h_nuit_normal || 0) > 0 && <Badge label={`${fmt2(j.h_nuit_normal)}h nuit`} color="#0D47A1" />}
                      {j.nb_paniers > 0 && <Badge label={`${j.nb_paniers} panier`} color="#558B2F" />}
                      {anomalie && <Badge label="Sans départ" color="#C62828" />}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Heures supp ── */}
          {tabEmp === 'hs' && (
            <div style={{ padding: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ background: '#FFF3E0', borderRadius: 12, padding: 20, textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>HS majorées à 15%</div>
                  <div style={{ fontSize: 32, fontWeight: 800, color: '#F57F17' }}>{fmt2(myRecap.h_hs_15)}</div>
                  <div style={{ fontSize: 11, color: '#999' }}>heures</div>
                </div>
                <div style={{ background: '#FBE9E7', borderRadius: 12, padding: 20, textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>HS majorées à 40%</div>
                  <div style={{ fontSize: 32, fontWeight: 800, color: '#BF360C' }}>{fmt2(myRecap.h_hs_40)}</div>
                  <div style={{ fontSize: 11, color: '#999' }}>heures</div>
                </div>
              </div>
              <div style={{ marginTop: 16, padding: '12px 16px', background: '#FFFDE7', borderRadius: 10, fontSize: 13, color: '#F57F17', border: '1px solid #FFF176' }}>
                Total heures supplémentaires : <b>{fmt2(parseFloat(myRecap.h_hs_15 || 0) + parseFloat(myRecap.h_hs_40 || 0))}h</b>
              </div>
            </div>
          )}

          {/* ── Paniers ── */}
          {tabEmp === 'paniers' && (
            <div style={{ padding: 20 }}>
              <div style={{ background: '#F1F8E9', borderRadius: 14, padding: '24px 20px', textAlign: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: '#555', marginBottom: 8 }}>Paniers repas cette semaine</div>
                <div style={{ fontSize: 48, fontWeight: 800, color: '#558B2F', lineHeight: 1 }}>{Math.round(myRecap.nb_paniers || 0)}</div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>paniers</div>
              </div>
              <p style={{ color: '#888', fontSize: 13, lineHeight: 1.6 }}>
                Les paniers repas sont attribués pour chaque journée de présence effective complète.
              </p>
            </div>
          )}

          {/* ── Absences ── */}
          {tabEmp === 'absences' && (
            <div style={{ padding: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div style={{ background: '#FFF8E1', borderRadius: 12, padding: 20, textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Absences justifiées</div>
                  <div style={{ fontSize: 32, fontWeight: 800, color: '#FF8F00' }}>{fmt2(myRecap.h_absent_just)}</div>
                  <div style={{ fontSize: 11, color: '#999' }}>heures</div>
                </div>
                <div style={{ background: '#FFEBEE', borderRadius: 12, padding: 20, textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Absences non justifiées</div>
                  <div style={{ fontSize: 32, fontWeight: 800, color: '#C62828' }}>{fmt2(myRecap.h_absent_injust)}</div>
                  <div style={{ fontSize: 11, color: '#999' }}>heures</div>
                </div>
              </div>
              {parseFloat(myRecap.h_conge || 0) > 0 && (
                <div style={{ background: '#E3F2FD', borderRadius: 10, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: '#1565C0' }}>Congés cette semaine</span>
                  <span style={{ fontWeight: 800, color: '#1565C0', fontSize: 18 }}>{fmt2(myRecap.h_conge)}h</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Vue Export ───────────────────────────────────────────────────────────────
function VueExportContent({ siteId, setSiteId, sites, token, showToast, peutVoirTout, semaine }) {
  const [periodeActive, setPeriodeActive] = useState(null);
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin]   = useState('');
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const [exportingPdf, setExportingPdf]   = useState(false);

  const semaineCloturee = semaine?.statut === 'cloture';

  const PERIODES = [
    { id: 'hebdo',     label: 'Hebdomadaire', desc: "Données d'une semaine",    badge: '7J',  color: '#1B5E20' },
    { id: 'mensuel',   label: 'Mensuel',       desc: 'Résumé par mois',          badge: '1M',  color: '#1565C0' },
    { id: 'semestriel',label: 'Semestriel',    desc: 'Vue sur 6 mois',           badge: '6M',  color: '#6A1B9A' },
    { id: 'annuel',    label: 'Annuel',        desc: 'Rapport annuel complet',   badge: '1A',  color: '#BF360C' },
  ];

  const handleExportBinaire = async (format) => {
    if (!semaine?.id || !semaineCloturee) return;
    const setLoading = format === 'excel' ? setExportingXlsx : setExportingPdf;
    setLoading(true);
    try {
      const params = new URLSearchParams({ semaine_id: semaine.id });
      if (siteId) params.append('site_id', siteId);
      const resp = await axios.get(`${API}/api/temps/export/${format}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      const cd = resp.headers['content-disposition'] || '';
      a.download = cd.match(/filename="([^"]+)"/)?.[1] || `export.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 200);
      showToast(`Export ${format === 'excel' ? 'Excel' : 'PDF'} téléchargé`, 'success');
    } catch { showToast(`Erreur export ${format}`, 'error'); }
    finally { setLoading(false); }
  };

  const handleExportJson = async () => {
    try {
      const params = new URLSearchParams({ periode: periodeActive || 'hebdo' });
      if (siteId) params.append('site_id', siteId);
      if (dateDebut) params.append('date_debut', dateDebut);
      if (dateFin)   params.append('date_fin', dateFin);
      const { data } = await axios.get(`${API}/api/temps/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `export_temps_${periodeActive}_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      showToast('Export JSON téléchargé', 'success');
    } catch { showToast("Erreur lors de l'export", 'error'); }
  };

  const periodeObj = PERIODES.find(p => p.id === periodeActive);
  const btnExport = (actif, loading, label, icon, onClick, style) => ({
    onClick: actif && !loading ? onClick : undefined,
    disabled: !actif,
    title: !actif ? "Clôturez la semaine pour activer l'export" : '',
    style: { ...style, opacity: actif ? 1 : 0.4, cursor: actif ? 'pointer' : 'not-allowed',
             display: 'inline-flex', alignItems: 'center', gap: 7, borderRadius: 8,
             padding: '9px 18px', fontWeight: 700, fontSize: 13, border: 'none' },
  });

  return (
    <div>
      {/* ── Export structuré semaine clôturée ── */}
      <div style={{ background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h3 style={{ margin: 0, color: '#1B5E20', fontSize: 15, fontWeight: 700 }}>Export officiel SOGAS-RH</h3>
          <span style={{ background: semaineCloturee ? '#E8F5E9' : '#FFF3E0', color: semaineCloturee ? '#1B5E20' : '#E65100', padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
            {semaineCloturee ? 'Semaine clôturée' : 'Semaine en cours'}
          </span>
        </div>
        {semaine ? (
          <p style={{ margin: '0 0 14px', color: '#666', fontSize: 13 }}>
            Semaine du {new Date(semaine.date_debut).toLocaleDateString('fr-FR')} au {new Date(semaine.date_fin).toLocaleDateString('fr-FR')}
          </p>
        ) : (
          <p style={{ margin: '0 0 14px', color: '#aaa', fontSize: 13 }}>Aucune semaine chargée</p>
        )}
        {!semaineCloturee && (
          <div style={{ background: '#FFF3E0', borderRadius: 8, padding: '10px 14px', marginBottom: 14, color: '#E65100', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            Clôturez la semaine pour activer l'export Excel et PDF.
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button {...btnExport(semaineCloturee, exportingXlsx, 'Excel', null, () => handleExportBinaire('excel'),
            { background: semaineCloturee ? 'transparent' : '#f5f5f5', color: semaineCloturee ? '#1B5E20' : '#aaa', border: `2px solid ${semaineCloturee ? '#1B5E20' : '#ddd'}` })}>
            {exportingXlsx
              ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{animation:'spin 1s linear infinite'}}><circle cx="12" cy="12" r="10"/><path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/></svg>
              : <Download size={14} />}
            {exportingXlsx ? 'Génération...' : 'Exporter Excel'}
          </button>
          <button {...btnExport(semaineCloturee, exportingPdf, 'PDF', null, () => handleExportBinaire('pdf'),
            { background: semaineCloturee ? '#1B5E20' : '#f5f5f5', color: semaineCloturee ? '#fff' : '#aaa' })}>
            {exportingPdf
              ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{animation:'spin 1s linear infinite'}}><circle cx="12" cy="12" r="10"/><path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/></svg>
              : <FileText size={14} />}
            {exportingPdf ? 'Génération...' : 'Exporter PDF'}
          </button>
        </div>
        <div style={{ marginTop: 12, fontSize: 11, color: '#aaa', display: 'flex', alignItems: 'center', gap: 4 }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Chaque export est tracé dans le journal d'audit SOGAS-RH. Aucun montant financier inclus.
        </div>
      </div>

      {/* ── Export données brutes (JSON) ── */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: '#888', fontWeight: 600, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Données brutes</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
          {PERIODES.map(p => (
            <button key={p.id} onClick={() => setPeriodeActive(p.id)} style={{
              background: periodeActive === p.id ? p.color : '#fff',
              borderRadius: 14, padding: '18px 14px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              border: `2px solid ${periodeActive === p.id ? p.color : '#e0e0e0'}`,
              cursor: 'pointer', textAlign: 'left',
            }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: periodeActive === p.id ? '#fff' : p.color, marginBottom: 6, lineHeight: 1 }}>{p.badge}</div>
              <div style={{ fontWeight: 700, fontSize: 14, color: periodeActive === p.id ? '#fff' : '#333', marginBottom: 3 }}>{p.label}</div>
              <div style={{ fontSize: 11, color: periodeActive === p.id ? 'rgba(255,255,255,0.75)' : '#999' }}>{p.desc}</div>
            </button>
          ))}
        </div>
        {periodeActive && (
          <div style={{ background: '#fff', borderRadius: 14, padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <h3 style={{ margin: '0 0 18px', color: '#1B5E20', fontSize: 15 }}>Paramètres — {periodeObj?.label}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div><label style={lbS}>Date de début</label><input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} style={inS} /></div>
              <div><label style={lbS}>Date de fin</label><input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} style={inS} /></div>
            </div>
            {peutVoirTout && (
              <div style={{ marginBottom: 16 }}>
                <label style={lbS}>Site</label>
                <select value={siteId} onChange={e => setSiteId(e.target.value)} style={inS}>
                  <option value="">Tous les sites</option>
                  {Array.isArray(sites) && sites.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
                </select>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleExportJson} style={{ ...btnSec, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Download size={14} />Exporter JSON
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────
export default function GestionTempsPage({ lectureSeule = false }) {
  const { employe, token } = useAuth();
  const role = employe?.role || '';
  const estRH = ROLES_RH.includes(role);
  const permsVivantes = employe?.permissions_effectives || {};

  const peutVoirTemps = estRH || permsVivantes.peut_voir_temps_site === true || permsVivantes.peut_voir_tous_temps === true;
  // Chantier temps (agent I) : « Historique (essais) » en lecture seule — aucune saisie ni clôture.
  const peutSaisir    = !lectureSeule && (estRH || permsVivantes.peut_saisir_heures    === true);
  const peutCloturer  = !lectureSeule && (estRH || permsVivantes.peut_cloturer_semaine === true);
  const peutExporter  = estRH || permsVivantes.peut_exporter_temps   === true;
  const peutVoirTout  = estRH || permsVivantes.peut_voir_tous_temps  === true;

  const estEmploye = role === 'employe';
  const estResponsable = !estRH && peutVoirTemps;

  const ongletsDispo = [
    ...(estRH          ? [{ id: 'rh',          label: 'Vue RH' }]        : []),
    ...(estResponsable ? [{ id: 'responsable',  label: 'Mon Site' }]     : []),
    ...(estEmploye     ? [{ id: 'employe',      label: 'Mon Temps' }]    : []),
    ...(peutExporter   ? [{ id: 'export',       label: 'Exporter' }]     : []),
  ];

  const ongletDefaut = estEmploye ? 'employe'
    : estResponsable ? 'responsable'
    : estRH ? 'rh' : 'employe';

  const [onglet, setOnglet] = useState(ongletDefaut);
  const [sites, setSites]   = useState([]);
  const [siteId, setSiteId] = useState('');
  const [semaine, setSemaine]   = useState(null);
  const [recaps, setRecaps]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [offsetSemaine, setOffsetSemaine] = useState(0);
  const [employe_detail, setEmployeDetail] = useState(null);
  const [jourCorrection, setJourCorrection] = useState(null);
  const [toast, setToast]       = useState(null);
  const [exportingRhXlsx, setExportingRhXlsx] = useState(false);
  const [exportingRhPdf,  setExportingRhPdf]  = useState(false);

  const showToast = (msg, type = 'success') => setToast({ msg, type });

  const dateRef = useCallback(() => {
    const d = new Date();
    d.setDate(d.getDate() + offsetSemaine * 7);
    return d.toISOString().split('T')[0];
  }, [offsetSemaine]);

  // Module 0 (clôture) : seuls les sites du périmètre réel sont proposés ; un périmètre d'un
  // seul site le sélectionne d'office.
  const { perimetre: perimetreTemps, sites: sitesAutorises } = usePerimetre('peut_voir_temps_site');
  useEffect(() => {
    setSites(sitesAutorises);
    if (perimetreTemps && !perimetreTemps.global && sitesAutorises.length >= 1 && !siteId) setSiteId(sitesAutorises[0].id);
  }, [perimetreTemps, sitesAutorises]);

  const charger = useCallback(async () => {
    if (onglet === 'employe') { setLoading(false); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (siteId) params.append('site_id', siteId);
      params.append('date', dateRef());
      const { data: sem } = await axios.get(
        `${API}/api/temps/semaine-en-cours?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSemaine(sem.semaine);
      if (sem.semaine?.id) {
        const recapParams = siteId ? `?site_id=${siteId}` : '';
        const { data: recap } = await axios.get(
          `${API}/api/temps/semaine/${sem.semaine.id}${recapParams}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setRecaps(recap.recaps || []);
      } else {
        setRecaps([]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [siteId, dateRef, token, onglet]);

  useEffect(() => { charger(); }, [charger]);

  // C0 (mesure 8, décision C0-a) : la clôture hebdomadaire est suspendue côté serveur (409
  // CLOTURE_SUSPENDUE). Le bouton affiche le message au lieu de lancer la clôture.
  const handleCloturer = () => {
    showToast(MESSAGE_CLOTURE_SUSPENDUE, 'info');
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({ periode: 'semaine' });
      if (siteId) params.append('site_id', siteId);
      const { data } = await axios.get(`${API}/api/temps/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `export_semaine_${semaine?.date_debut || 'inconnu'}.json`;
      a.click();
      showToast('Export téléchargé', 'success');
    } catch { showToast("Erreur lors de l'export", 'error'); }
  };

  const handleExportRH = async (format) => {
    const setLoading = format === 'excel' ? setExportingRhXlsx : setExportingRhPdf;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (semaine?.id) params.append('semaine_id', semaine.id);
      if (siteId) params.append('site_id', siteId);
      const resp = await axios.get(`${API}/api/temps/export/${format}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const cd = resp.headers['content-disposition'] || '';
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.href = url;
      a.download = cd.match(/filename="([^"]+)"/)?.[1] || `export.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 200);
      showToast(`Export ${format === 'excel' ? 'Excel' : 'PDF'} téléchargé`, 'success');
    } catch { showToast(`Erreur export ${format}`, 'error'); }
    finally { setLoading(false); }
  };

  const semaineCloturee = semaine?.statut === 'cloture';
  const labelSemaine = semaine
    ? `${new Date(semaine.date_debut).toLocaleDateString('fr-FR')} → ${new Date(semaine.date_fin).toLocaleDateString('fr-FR')}`
    : '…';

  const totaux = recaps.reduce((acc, r) => {
    COLONNES.forEach(c => { acc[c.key] = (acc[c.key] || 0) + parseFloat(r[c.key] || 0); });
    return acc;
  }, {});

  const nbHS     = recaps.filter(r => parseFloat(r.h_hs_15 || 0) + parseFloat(r.h_hs_40 || 0) > 0).length;
  const nbPaniers = Math.round(totaux.nb_paniers || 0);
  const nbNuit   = recaps.filter(r => parseFloat(r.h_nuit_normal || 0) + parseFloat(r.h_nuit_dim || 0) > 0).length;
  const nbAbsInj = recaps.filter(r => parseFloat(r.h_absent_injust || 0) > 0).length;
  const totNuit  = parseFloat(totaux.h_nuit_normal || 0) + parseFloat(totaux.h_nuit_dim || 0);
  const totHS    = parseFloat(totaux.h_hs_15 || 0) + parseFloat(totaux.h_hs_40 || 0);

  const myRecap = estEmploye
    ? (recaps.find(r => r.employe_id === employe.id) || recaps[0] || null)
    : null;

  if (!peutVoirTemps && !estEmploye) {
    return (
      <Layout>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60, color: '#888' }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5" style={{ marginBottom: 16 }}>
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#555', marginBottom: 8 }}>Accès non autorisé</div>
          <div style={{ fontSize: 13, color: '#999' }}>Vous n'avez pas accès au module Gestion du Temps.</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {lectureSeule && (
        <div className="rh-page" style={{ paddingBottom: 0 }}>
          <NavTemps actif="historique" />
          <BandeauInfo ton="info" titre="Historique des essais — lecture seule">
            Ancien écran du temps, conservé pour consultation : les données des essais ne sont plus calculées ni modifiables.
            Le suivi actuel est dans « Journées » et « Clôture et paie ».
          </BandeauInfo>
        </div>
      )}
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
        @media(max-width:768px){.t-scroll{overflow-x:auto;}}
      `}</style>

      <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif', minHeight: '100vh', background: '#f7f7f7' }}>

        {/* ══ HEADER ══ */}
        <div className="sg-bandeau-clair" style={{ background: '#1B5E20', padding: '14px 18px 18px', position: 'sticky', top: 0, zIndex: 100, overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, background: 'rgba(255,255,255,0.04)', borderRadius: '50%', pointerEvents: 'none' }} />

          {/* Ligne 1 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <img src="/logo-sogas1.png" alt="SOGAS"
              style={{ height: 28, objectFit: 'contain' }}
              onError={e => { e.target.onerror = null; e.target.src = '/logo-sogas.png'; }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <NotificationBell couleurIcone="#fff" taille={16} />
              {onglet !== 'export' && onglet !== 'employe' && semaine && (
                <div style={{
                  background: semaineCloturee ? 'rgba(165,214,167,0.3)' : 'rgba(255,193,7,0.3)',
                  border: `1px solid ${semaineCloturee ? '#A5D6A7' : '#FFC107'}`,
                  borderRadius: 20, padding: '3px 10px', fontSize: '0.65rem', fontWeight: 700, color: '#fff',
                }}>
                  {semaineCloturee ? 'CLÔTURÉE' : 'OUVERTE'}
                </div>
              )}
            </div>
          </div>

          {/* Ligne 2 : titre + navigateur */}
          {onglet !== 'employe' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
              <p style={{ margin: 0, color: '#fff', fontWeight: 700, fontSize: '0.95rem', flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                {onglet === 'export' ? <Download size={16} /> : <Clock size={16} />}
                {onglet === 'export' ? "Centre d'Export" : 'Gestion du Temps'}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '4px 8px' }}>
                <button onClick={() => setOffsetSemaine(o => o - 1)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, padding: '0 2px' }}>‹</button>
                <span style={{ fontSize: '0.72rem', whiteSpace: 'nowrap', color: '#fff', fontWeight: 600 }}>{labelSemaine}</span>
                <button onClick={() => setOffsetSemaine(o => o + 1)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, padding: '0 2px' }}>›</button>
                {offsetSemaine !== 0 && (
                  <button onClick={() => setOffsetSemaine(0)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '0.6rem', padding: '2px 6px', borderRadius: 4 }}>Auj.</button>
                )}
              </div>
            </div>
          )}

          {/* Ligne 3 : filtres Vue RH */}
          {onglet === 'rh' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 10 }}>
              {peutVoirTout && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
                  <button onClick={() => setSiteId('')}
                    style={{ background: !siteId ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', borderRadius: 20, padding: '3px 10px', fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer' }}>
                    Tous
                  </button>
                  {Array.isArray(sites) && sites.map(s => (
                    <button key={s.id} onClick={() => setSiteId(s.id)}
                      style={{ background: siteId === s.id ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', borderRadius: 20, padding: '3px 10px', fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer' }}>
                      {s.nom || s.code}
                    </button>
                  ))}
                </div>
              )}
              {peutCloturer && (
                <button
                  onClick={!semaineCloturee ? handleCloturer : undefined}
                  disabled={semaineCloturee}
                  title={semaineCloturee ? '' : MESSAGE_CLOTURE_SUSPENDUE}
                  style={{ background: 'rgba(100,100,100,0.4)', color: '#fff', border: 'none', borderRadius: 8, padding: '5px 12px', cursor: semaineCloturee ? 'not-allowed' : 'pointer', fontSize: '0.72rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  {semaineCloturee ? <Lock size={12} /> : <Unlock size={12} />}
                  {semaineCloturee ? 'Clôturée' : 'Clôture suspendue'}
                </button>
              )}
            </div>
          )}

          {/* Ligne 3 : Vue Responsable — badge site + anomalies */}
          {onglet === 'responsable' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 10 }}>
              <span style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 20, padding: '3px 12px', fontSize: '0.7rem', color: '#fff', fontWeight: 600 }}>
                {(Array.isArray(sites) && sites.find(s => s.id === siteId)?.nom) || 'Mon site'}
              </span>
              {nbAbsInj > 0 && (
                <span style={{ background: 'rgba(198,40,40,0.45)', border: '1px solid rgba(255,100,100,0.4)', borderRadius: 20, padding: '3px 12px', fontSize: '0.7rem', color: '#fff', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <AlertTriangle size={11} />{nbAbsInj} anomalie{nbAbsInj > 1 ? 's' : ''} signalée{nbAbsInj > 1 ? 's' : ''}
                </span>
              )}
            </div>
          )}
        </div>

        {/* ══ ONGLETS ══ */}
        {ongletsDispo.length > 1 && (
          <TabBar onglets={ongletsDispo} actif={onglet} onChange={setOnglet} />
        )}

        {/* ══ CONTENU ══ */}
        <div style={{ padding: '12px 14px' }}>

          {/* ── Vue RH ── */}
          {onglet === 'rh' && (
            <>
              {!loading && recaps.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 12 }}>
                  <StatCard label="Employés pointés"   value={recaps.length} color="#1B5E20" />
                  <StatCard label="HS détectées"        value={nbHS}          color="#F57F17" sub={`${fmt2(totHS)}h total`} />
                  <StatCard label="Heures de nuit"      value={nbNuit}        color="#0D47A1" sub={`${fmt2(totNuit)}h total`} />
                  <StatCard label="Paniers repas"       value={nbPaniers}     color="#558B2F" />
                  <StatCard label="Abs. non justifiées" value={nbAbsInj}      color="#C62828" />
                </div>
              )}
              <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                <div className="t-scroll">
                  <TableauSemaine
                    recaps={recaps} loading={loading} semaineCloturee={semaineCloturee}
                    totaux={totaux} setEmployeDetail={setEmployeDetail}
                  />
                </div>
                {!loading && (
                  <div style={{ padding: '12px 20px', borderTop: '1px solid #f0f0f0', display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', gap: 20, fontSize: 13, color: '#555', flexWrap: 'wrap' }}>
                      <span><b>{recaps.length}</b> employés</span>
                      <span><b>{nbHS}</b> HS</span>
                      <span><b>{nbPaniers}</b> paniers</span>
                      <span><b>{nbAbsInj}</b> abs. injust.</span>
                    </div>
                    {peutExporter && (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => handleExportRH('excel')}
                          disabled={!semaineCloturee || exportingRhXlsx}
                          title={!semaineCloturee ? "Clôturez la semaine pour activer l'export" : ''}
                          style={{ ...btnSec, display: 'inline-flex', alignItems: 'center', gap: 5, opacity: !semaineCloturee ? 0.45 : 1, cursor: !semaineCloturee ? 'not-allowed' : 'pointer' }}
                        >
                          <Download size={13} />{exportingRhXlsx ? 'Génération...' : 'Excel'}
                        </button>
                        <button
                          onClick={() => handleExportRH('pdf')}
                          disabled={!semaineCloturee || exportingRhPdf}
                          title={!semaineCloturee ? "Clôturez la semaine pour activer l'export" : ''}
                          style={{ ...btnSec, display: 'inline-flex', alignItems: 'center', gap: 5, opacity: !semaineCloturee ? 0.45 : 1, cursor: !semaineCloturee ? 'not-allowed' : 'pointer' }}
                        >
                          <Download size={13} />{exportingRhPdf ? 'Génération...' : 'PDF'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── Vue Responsable ── */}
          {onglet === 'responsable' && (
            <>
              <BandeauPerimetre perimetre={perimetreTemps} style={{ marginBottom: 10 }} />
              <div style={{ background: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: 10, padding: '10px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#2E7D32' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                Vue limitée à votre site. Contactez les RH pour accéder à tous les sites.
              </div>
              {!loading && recaps.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 12 }}>
                  <StatCard label="Employés"      value={recaps.length} color="#1B5E20" />
                  <StatCard label="HS détectées"  value={nbHS}          color="#F57F17" />
                  <StatCard label="Abs. non just." value={nbAbsInj}     color="#C62828" />
                </div>
              )}
              <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                <div className="t-scroll">
                  <TableauSemaine
                    recaps={recaps} loading={loading} semaineCloturee={semaineCloturee}
                    totaux={totaux} setEmployeDetail={setEmployeDetail}
                  />
                </div>
                {!loading && peutExporter && (
                  <div style={{ padding: '12px 20px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button
                      onClick={() => handleExportRH('excel')}
                      disabled={!semaineCloturee || exportingRhXlsx}
                      title={!semaineCloturee ? "Clôturez la semaine pour activer l'export" : ''}
                      style={{ ...btnSec, display: 'inline-flex', alignItems: 'center', gap: 5, opacity: !semaineCloturee ? 0.45 : 1, cursor: !semaineCloturee ? 'not-allowed' : 'pointer' }}
                    >
                      <Download size={13} />{exportingRhXlsx ? 'Génération...' : 'Excel'}
                    </button>
                    <button
                      onClick={() => handleExportRH('pdf')}
                      disabled={!semaineCloturee || exportingRhPdf}
                      title={!semaineCloturee ? "Clôturez la semaine pour activer l'export" : ''}
                      style={{ ...btnSec, display: 'inline-flex', alignItems: 'center', gap: 5, opacity: !semaineCloturee ? 0.45 : 1, cursor: !semaineCloturee ? 'not-allowed' : 'pointer' }}
                    >
                      <Download size={13} />{exportingRhPdf ? 'Génération...' : 'PDF'}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── Vue Employé ── */}
          {onglet === 'employe' && (
            <VueEmployeContent
              employe={employe}
              myRecap={myRecap}
              loading={loading}
              offsetSemaine={offsetSemaine}
              setOffsetSemaine={setOffsetSemaine}
              labelSemaine={labelSemaine}
            />
          )}

          {/* ── Vue Export ── */}
          {onglet === 'export' && (
            <VueExportContent
              siteId={siteId}
              setSiteId={setSiteId}
              sites={sites}
              token={token}
              showToast={showToast}
              peutVoirTout={peutVoirTout}
              semaine={semaine}
            />
          )}
        </div>

        {/* Panneau détail */}
        {employe_detail && (
          <>
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 1400 }}
              onClick={() => setEmployeDetail(null)} />
            <PanneauDetail
              recap={employe_detail}
              semaineCloturee={semaineCloturee}
              peutSaisir={peutSaisir}
              onClose={() => setEmployeDetail(null)}
              onCorrection={j => setJourCorrection(j)}
              onSaved={(msg, type) => { showToast(msg, type); charger(); }}
              token={token}
            />
          </>
        )}

        {jourCorrection && (
          <ModalCorrection
            jour={jourCorrection}
            token={token}
            onClose={() => setJourCorrection(null)}
            onSaved={(msg, type) => { showToast(msg, type); charger(); setEmployeDetail(null); }}
          />
        )}

        {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
      </div>
    </Layout>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const thS = { padding: '10px 12px', textAlign: 'left', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' };
const tdS = { padding: '10px 12px', fontSize: 13 };
const lbS = { display: 'block', fontSize: 12, color: '#555', marginBottom: 4, marginTop: 10, fontWeight: 600 };
const inS = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' };
const btnPri = { background: '#1B5E20', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', cursor: 'pointer', fontWeight: 700, fontSize: 13 };
const btnSec = { background: '#f5f5f5', color: '#333', border: '1px solid #ddd', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13 };
